import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormHelperText, Grid } from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import { useGetSignedUrl, useUploadFiles } from './FileUpload.queries';
import imageCompression from 'browser-image-compression';
import FileDisplay from './FileDisplay';

const useStyles = makeStyles(() => ({
    message: {
        width: '80%',
        textAlign: 'center',
    },
    dropZoneContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        margin: 0,
        width: '300x',
        height: '130px',
        border: '3px dashed #4aa1f3',
        borderRadius: '4px',
    },
    thumb: {
        display: 'inline-flex',
        width: 100,
        height: 100,
        padding: 4,
    },
    img: {
        display: 'block',
        width: 'auto',
        height: '100%',
    },
}));

const imageCompressionOptions = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
};

const DropzoneMulti = ({ handleDrop = () => {}, values, s3Directory, ...props }) => {
    const [files, setFiles] = useState([]);
    const [err, setError] = useState(null);
    const [status, setStatus] = useState('idle');
    const [uploadProgress, setUploadProgress] = useState(0);
    const uploadFile = useUploadFiles();
    const getSignedUrl = useGetSignedUrl();
    const classes = useStyles();

    useEffect(() => {
        if (values) {
            getRefreshedSignedUrl(values);
        }
    }, []);

    const getRefreshedSignedUrl = async (v) => {
        const valuesWithRefreshedUrl = await Promise.all(
            v.map(async (value) => {
                const { signedUrl } = await getSignedUrl.mutateAsync({ url: value.url });
                return { ...value, url: signedUrl };
            })
        );
        setFiles(valuesWithRefreshedUrl);
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: [
            'image/*',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv',
            // wrap
        ],
        multiple: true,
        maxFiles: 10,
        maxSize: 100000000, // 100 Mb
        onDrop: (acceptedFiles, fileRejections) => {
            if (fileRejections.length > 0) {
                fileRejections.forEach((file) => {
                    file.errors.forEach((e) => {
                        if (e.code === 'file-too-large') {
                            setError('Error uploading files. File is larger than 100Mb');
                        } else if (e.code === 'file-invalid-type') {
                            setError('Error uploading files. File type is not accepted');
                        } else {
                            setError('Error uploading files. Please try again or contact support if the problem persists');
                        }
                    });
                });
            } else {
                setError(null);
            }
            if (acceptedFiles.length > 0) {
                setFiles([...files, ...acceptedFiles]);
                handleUploadFiles(acceptedFiles);
            }
        },
        onError: (error) => {
            setError(error);
        },
        disabled: status === 'uploading',
    });

    const handleUploadFiles = async (acceptedFiles) => {
        const formData = new FormData();
        await Promise.all(
            acceptedFiles.map(async (file) => {
                // attach s3Directory to file name to allow for storing in the correct s3 directory
                if (isImage(file)) {
                    const compressedFile = await imageCompression(file, imageCompressionOptions);
                    formData.append('files', new File([compressedFile], `${s3Directory}/${file.name}`, { type: file.type }));
                } else {
                    formData.append('files', new File([file], `${s3Directory}/${file.name}`, { type: file.type }));
                }
            })
        );
        setStatus('uploading');
        setUploadProgress(0);
        uploadFile.mutate(
            { formData, setUploadProgress },
            {
                onSuccess: (data) => {
                    setFiles([...files, ...data.keys]);
                    handleDrop([...files, ...data.keys]);
                    setUploadProgress(100);
                    setStatus('idle');
                },
                onError: () => {
                    setError('Error uploading files. Please try again or contact support if the problem persists');
                    setUploadProgress(0);
                    setStatus('idle');
                    setFiles([...files]);
                },
            }
        );
    };

    const removeFile = (fileIndex) => () => {
        const newFiles = [...files];
        newFiles.splice(fileIndex, 1);
        setFiles(newFiles);
        handleDrop(newFiles);
    };

    const isImage = (file) => file.type?.includes('image');

    return (
        <>
            <Grid container sx={{ marginTop: '1rem' }}>
                <Grid item xs={12}>
                    <section className="container">
                        <div {...getRootProps({ className: classes.dropZoneContainer })}>
                            <input {...getInputProps()} />
                            <p className={classes.message}>Drag and drop some files here, or click to select files</p>
                        </div>
                        <FormHelperText error={!!err}>{err}</FormHelperText>
                    </section>
                </Grid>
            </Grid>
            {files?.map((file, index) => (
                <FileDisplay
                    file={file}
                    index={index}
                    classes={classes}
                    uploadProgress={uploadProgress}
                    removeFile={removeFile}
                    // wrap
                />
            ))}
        </>
    );
};

export default DropzoneMulti;
